import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { apiService } from '@/services/apiService';
import { widgetService } from '@/services/widgetService';
import { calculateSobrietyDays } from '@/utils/sobrietyDays';

// ============================================================================
// SOBRIETY CONTEXT
// Manages sobriety tracking data using server-side APIs for reliability
// All data operations go through Express server to bypass RLS timing issues
// ============================================================================

interface SobrietyRecord {
  id: string;
  user_id: string;
  start_date: string;
  base_days: number;
  setup_type: 'starting-today' | 'existing-days' | 'relapse';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Relapse {
  id: string;
  user_id: string;
  sobriety_record_id: string;
  relapse_date: string;
  days_sober_before_relapse: number;
  notes?: string;
  created_at: string;
}

interface SobrietyState {
  isSetup: boolean;
  currentRecord: SobrietyRecord | null;
  relapses: Relapse[];
  loading: boolean;
}

interface SobrietyContextType {
  sobrietyState: SobrietyState;
  setupSobrietyToday: (onComplete?: (setupType: 'starting-today') => void) => Promise<void>;
  setupSobrietyExisting: (days: number, onComplete?: (setupType: 'existing-days', days: number) => void) => Promise<void>;
  setupSobrietyRelapse: (onComplete?: (setupType: 'relapse') => void) => Promise<void>;
  getCurrentDays: () => number;
  refreshSobrietyData: () => Promise<void>;
}

const SobrietyContext = createContext<SobrietyContextType | undefined>(undefined);

export function SobrietyProvider({ children }: { children: ReactNode }) {
  // CRITICAL FIX: Get session and sessionReady from AuthContext
  // sessionReady indicates the session has been validated and is safe for API calls
  const { user, session, sessionReady } = useAuth();
  const [sobrietyState, setSobrietyState] = useState<SobrietyState>({
    isSetup: false,
    currentRecord: null,
    relapses: [],
    loading: true,
  });

  useEffect(() => {
    if (user && sessionReady && session?.access_token) {
      // Only load when session is validated and ready
      loadSobrietyData();
    } else if (user && !sessionReady) {
      // User exists but session not validated yet - wait for validation
      console.log('SobrietyContext: User logged in but waiting for session validation...');
    } else {
      setSobrietyState({
        isSetup: false,
        currentRecord: null,
        relapses: [],
        loading: false,
      });
    }
  }, [user, session, sessionReady]);

  // Load sobriety data via API
  const loadSobrietyData = async () => {
    if (!user) {
      console.log('No user, skipping sobriety data load');
      return;
    }

    try {
      setSobrietyState(prev => ({ ...prev, loading: true }));

      const response = await apiService.get('/api/sobriety');

      if (!response.success) {
        console.error('Sobriety API error:', response.error);
        setSobrietyState(prev => ({ ...prev, loading: false }));
        return;
      }

      setSobrietyState({
        isSetup: response.data.isSetup,
        currentRecord: response.data.currentRecord,
        relapses: response.data.relapses || [],
        loading: false,
      });

      // Sync sobriety data to widget shared storage (fire-and-forget, never throws)
      widgetService.syncSobrietyData(response.data.currentRecord, response.data.isSetup);

      console.log('Sobriety data loaded via API:', { 
        hasRecord: !!response.data.currentRecord, 
        relapseCount: response.data.relapses?.length || 0 
      });
    } catch (error) {
      console.error('Failed to load sobriety data:', error);
      setSobrietyState(prev => ({ ...prev, loading: false }));
    }
  };

  const refreshSobrietyData = async () => {
    await loadSobrietyData();
  };

  const setupSobrietyToday = async (onComplete?: (setupType: 'starting-today') => void) => {
    if (!user) {
      console.error('No user logged in');
      return;
    }

    try {
      const response = await apiService.post('/api/sobriety/start', {
        setup_type: 'starting-today',
        base_days: 0,
      });

      if (!response.success) {
        console.error('Failed to start sobriety:', response.error);
        return;
      }

      setSobrietyState(prev => ({
        ...prev,
        isSetup: true,
        currentRecord: response.data.record,
      }));

      await refreshSobrietyData();

      console.log('Setup sobriety: starting today');
      onComplete?.('starting-today');
    } catch (error) {
      console.error('Failed to setup sobriety (starting today):', error);
    }
  };

  const setupSobrietyExisting = async (days: number, onComplete?: (setupType: 'existing-days', days: number) => void) => {
    if (!user) {
      console.error('No user logged in');
      return;
    }

    try {
      console.log('Setting up sobriety with existing days:', days);

      const response = await apiService.post('/api/sobriety/start', {
        setup_type: 'existing-days',
        base_days: days,
      });

      if (!response.success) {
        console.error('Failed to start sobriety with existing days:', response.error);
        throw new Error(response.error || 'Failed to create sobriety record');
      }

      console.log('New sobriety record created via API:', response.data.record);

      setSobrietyState(prev => ({
        ...prev,
        isSetup: true,
        currentRecord: response.data.record,
      }));

      await refreshSobrietyData();

      console.log('Setup sobriety: existing days scenario completed', days);
      onComplete?.('existing-days', days);
    } catch (error) {
      console.error('Failed to setup sobriety (existing days):', error);
      throw error;
    }
  };

  const setupSobrietyRelapse = async (onComplete?: (setupType: 'relapse') => void) => {
    if (!user) {
      console.error('No user logged in');
      return;
    }

    try {
      const response = await apiService.post('/api/sobriety/relapse', {});

      if (!response.success) {
        console.error('Failed to record relapse:', response.error);
        return;
      }

      setSobrietyState(prev => ({
        ...prev,
        isSetup: true,
        currentRecord: response.data.record,
        relapses: response.data.relapses || prev.relapses,
      }));

      await refreshSobrietyData();

      console.log('Setup sobriety: relapse scenario', { daysSoberBeforeRelapse: response.data.daysSoberBeforeRelapse });
      onComplete?.('relapse');
    } catch (error) {
      console.error('Failed to setup sobriety (relapse):', error);
    }
  };

  const getCurrentDays = (): number => {
    if (!sobrietyState.currentRecord || sobrietyState.loading) {
      return 0;
    }

    // Single source of truth — see utils/sobrietyDays.ts
    return calculateSobrietyDays(
      new Date(sobrietyState.currentRecord.start_date),
      sobrietyState.currentRecord.base_days,
    );
  };

  return (
    <SobrietyContext.Provider
      value={{
        sobrietyState,
        setupSobrietyToday,
        setupSobrietyExisting,
        setupSobrietyRelapse,
        getCurrentDays,
        refreshSobrietyData,
      }}
    >
      {children}
    </SobrietyContext.Provider>
  );
}

export function useSobriety() {
  const context = useContext(SobrietyContext);
  if (context === undefined) {
    throw new Error('useSobriety must be used within a SobrietyProvider');
  }
  return context;
}
